import type React from 'react';
import { useState, useCallback } from 'react';

import { OrdEditDialog } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { RecordContextProvider, useDelete, useRefresh } from 'react-admin';

import { ResourceChipList } from 'src/components/resourceChipList';
import { AgentsForm } from 'src/views/Resources/admin/agents/AgentsForm';

import AgentChip from '../agents/AgentChip';

import type { Agent } from '@ragu-components/ord-types';

export interface QueueAgentsPreviewProps {
  /** The queue ID to fetch agents for */
  queueId: string;
  /** Action button element (passed from parent with RecordContext) */
  actionButton?: React.ReactNode;
}

/**
 * A preview card showing the agents assigned to a queue.
 * Displays agents as chips with avatars and provides a button to manage assignments.
 */
export const QueueAgentsPreview = ({ queueId, actionButton }: QueueAgentsPreviewProps): React.ReactElement => {
  const refresh = useRefresh();
  const [deleteOne] = useDelete();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const handleChipClick = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedAgent(null);
    refresh();
  }, [refresh]);

  const handleDelete = useCallback(
    (agent: Agent, event: React.MouseEvent) => {
      event.stopPropagation();
      void deleteOne(
        Resources.agents,
        { id: agent.id, previousData: agent },
        {
          onSuccess: () => {
            refresh();
          },
        }
      );
    },
    [deleteOne, refresh]
  );

  return (
    <>
      <ResourceChipList
        resource={Resources.agents}
        filter={{ active: 'fiql', fiql: `queue.id==${queueId}` }}
        actionButton={actionButton}
        emptyLabel='ra.navigation.no_results'
      >
        {(record) => (
          <AgentChip
            record={record as Agent}
            onClick={() => {
              handleChipClick(record as Agent);
            }}
            onDelete={(event) => {
              handleDelete(record as Agent, event as React.MouseEvent);
            }}
            sx={{
              cursor: 'pointer',
              '& .MuiChip-deleteIcon': { opacity: 0 },
              '&:hover .MuiChip-deleteIcon': { opacity: 1 },
            }}
          />
        )}
      </ResourceChipList>

      {selectedAgent !== null && (
        <RecordContextProvider value={selectedAgent}>
          <OrdEditDialog
            resource={Resources.agents}
            id={selectedAgent.id}
            isOpen={true}
            close={handleCloseDialog}
            mutationMode='pessimistic'
          >
            <AgentsForm />
          </OrdEditDialog>
        </RecordContextProvider>
      )}
    </>
  );
};

export default QueueAgentsPreview;
